import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DjBios from "../components/DjBios";
import {
  Mail,
  MessageSquare,
  MapPin,
  Send,
  Check,
  Phone,
  MessageCircle,
  Radio,
  Music4,
  Tv2,
  BookOpen,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CONTACT_EMAIL = "gfnsoulnycfm@gmail.com";
const CALL_NUMBER = "2142728286";
const CALL_DISPLAY = "(214) 272-8286";
const TEXT_NUMBER = "2149405814";
const TEXT_DISPLAY = "(214) 940-5814";

const NETWORKS = [
  {
    icon: Music4,
    name: "GFN Soul NYC FM 151",
    tag: "Old School Soul",
    desc: "Our flagship station featuring classic soul, R&B, old-school favorites, talk, and community programming.",
    to: "/fm151",
  },
  {
    icon: Radio,
    name: "GFN Soul NYC FM The Vibe",
    tag: "Hip-Hop & Today's R&B",
    desc: "Bringing today's Hip-Hop, R&B, and contemporary sounds to listeners everywhere.",
    to: "/vibe",
  },
  {
    icon: Tv2,
    name: "GFN Soul NYC TV",
    tag: "Live TV Network",
    desc: "Our television network featuring entertainment, talk shows, music, community programming, and original content.",
    to: "/gfn-tv",
  },
  {
    icon: BookOpen,
    name: "GFN Soul On Demand",
    tag: "Live TV · On Demand Catalog",
    desc: "The live GFN Soul NYC TV channel plus our full on-demand video catalog — search, sort, and watch anytime from the web app.",
    to: "/on-demand",
  },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          subject: form.subject.trim() || null,
          message: form.message.trim(),
        }),
      });
      if (res.ok) {
        setDone(true);
        toast.success("Message sent — we'll get back to you soon.");
      } else {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Something went wrong");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10">
      {/* ABOUT hero */}
      <header className="mb-10">
        <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 backdrop-blur-sm bg-black/40">
          <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
            About Us
          </span>
        </div>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl leading-[0.9] text-white uppercase">
          GFN Soul NYC <span className="text-gold-gradient">Networks</span>
        </h1>
        <p className="mt-4 font-body text-neutral-300 text-base md:text-lg max-w-3xl leading-relaxed">
          A powerful multimedia network connecting <span className="text-gold">music, television,
          entertainment, culture, community,</span> and inspirational programming.
        </p>
      </header>

      {/* Networks grid */}
      <section
        data-testid="about-networks"
        className="grid md:grid-cols-2 gap-4 mb-10"
      >
        {NETWORKS.map((n) => (
          <Link
            key={n.name}
            to={n.to}
            data-testid={`about-network-${n.to.replace("/", "")}`}
            className="group bg-jet-surface border border-gold/25 hover:border-gold/60 rounded-lg p-6 transition-colors block"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 grid place-items-center rounded border-gold-gradient">
                <n.icon className="w-5 h-5 text-gold" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold">
                  {n.tag}
                </div>
                <div className="mt-1 font-display text-xl md:text-2xl text-white uppercase leading-tight">
                  {n.name}
                </div>
                <p className="mt-2 font-body text-sm text-neutral-300 leading-relaxed">
                  {n.desc}
                </p>
                <div className="mt-3 font-condensed uppercase tracking-widest text-[10px] text-gold group-hover:text-gold-light">
                  Open →
                </div>
              </div>
            </div>
          </Link>
        ))}
      </section>

      {/* Tagline strip */}
      <section className="rounded-lg border border-gold/25 bg-gradient-to-r from-gold-dark/20 via-jet-surface/60 to-jet-surface/40 p-6 md:p-8 mb-14 text-center">
        <div className="font-display text-2xl md:text-3xl uppercase text-white leading-tight">
          Radio · Television · <span className="text-gold-gradient">Faith</span> · Culture · Community
        </div>
        <div className="mt-1 font-condensed uppercase tracking-[0.35em] text-[11px] text-gold">
          All in one network
        </div>
      </section>

      {/* DJ Bios */}
      <DjBios />

      {/* CONTACT hero */}
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 backdrop-blur-sm bg-black/40">
          <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
            Contact Us
          </span>
        </div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-[0.9] text-white uppercase">
          Talk to the <span className="text-gold-gradient">booth</span>
        </h2>
        <p className="mt-3 font-body text-neutral-300 text-base md:text-lg max-w-2xl">
          Sponsorships, artist submissions, press, or just to say what's up — reach out
          any way you like.
        </p>
      </header>

      {/* Quick contact cards */}
      <section className="grid sm:grid-cols-3 gap-4 mb-8">
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          data-testid="contact-quick-email"
          className="bg-jet-surface border border-gold/25 hover:border-gold rounded-lg p-5 transition-colors"
        >
          <div className="flex items-center gap-2 font-condensed uppercase tracking-widest text-[10px] text-gold">
            <Mail className="w-3.5 h-3.5" />
            Email
          </div>
          <div className="mt-2 font-display text-lg text-white break-all leading-tight">
            {CONTACT_EMAIL}
          </div>
        </a>
        <a
          href={`tel:${CALL_NUMBER}`}
          data-testid="contact-quick-call"
          className="bg-jet-surface border border-gold/25 hover:border-gold rounded-lg p-5 transition-colors"
        >
          <div className="flex items-center gap-2 font-condensed uppercase tracking-widest text-[10px] text-gold">
            <Phone className="w-3.5 h-3.5" />
            Call
          </div>
          <div className="mt-2 font-display text-lg text-white leading-tight">
            {CALL_DISPLAY}
          </div>
        </a>
        <a
          href={`sms:${TEXT_NUMBER}`}
          data-testid="contact-quick-text"
          className="bg-jet-surface border border-gold/25 hover:border-gold rounded-lg p-5 transition-colors"
        >
          <div className="flex items-center gap-2 font-condensed uppercase tracking-widest text-[10px] text-gold">
            <MessageCircle className="w-3.5 h-3.5" />
            Text
          </div>
          <div className="mt-2 font-display text-lg text-white leading-tight">
            {TEXT_DISPLAY}
          </div>
        </a>
      </section>

      {/* Form + side info */}
      <div className="grid md:grid-cols-[1.4fr_1fr] gap-6">
        <section className="bg-jet-surface border border-gold/25 rounded-lg p-6 md:p-8">
          {done ? (
            <div data-testid="contact-thankyou" className="min-h-[300px] grid place-items-center text-center">
              <div>
                <div className="mx-auto w-14 h-14 grid place-items-center rounded-full bg-gold/20 text-gold mb-3">
                  <Check className="w-6 h-6" />
                </div>
                <div className="font-display text-3xl text-white uppercase">Message Sent</div>
                <p className="mt-2 font-body text-neutral-400 max-w-md">
                  Thanks for reaching out. We'll get back to you at{" "}
                  <span className="text-gold">{form.email}</span> as soon as we can.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => {
                    setDone(false);
                    setForm({ name: "", email: "", subject: "", message: "" });
                  }}
                  className="mt-6 inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors"
                >
                  Send Another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                    Your name
                  </label>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    maxLength={80}
                    className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2.5 text-sm text-white outline-none"
                  />
                </div>
                <div>
                  <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                    Email
                  </label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    maxLength={200}
                    className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2.5 text-sm text-white outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                  Subject (optional)
                </label>
                <input
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value)}
                  placeholder="Sponsorship / Artist submission / Press / …"
                  maxLength={120}
                  className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2.5 text-sm text-white outline-none"
                />
              </div>
              <div>
                <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                  Message
                </label>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={7}
                  maxLength={4000}
                  className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2.5 text-sm text-white outline-none resize-y"
                />
              </div>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light disabled:opacity-50 text-black font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-colors"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </section>

        <aside className="space-y-4">
          <div className="bg-jet-surface border border-gold/20 rounded-lg p-5">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gold" />
              <div className="font-condensed uppercase tracking-widest text-[10px] text-gold">
                Live chat
              </div>
            </div>
            <div className="mt-2 font-body text-sm text-neutral-300">
              Tap <span className="text-gold">Live Chat</span> at the top of any page and drop a message in the room.
            </div>
          </div>
          <div className="bg-jet-surface border border-gold/20 rounded-lg p-5">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gold" />
              <div className="font-condensed uppercase tracking-widest text-[10px] text-gold">
                Broadcasting from
              </div>
            </div>
            <div className="mt-2 font-display text-xl text-white uppercase leading-tight">
              New York City
            </div>
            <div className="mt-1 font-body text-sm text-neutral-400">
              Global reach · Local impact · Real soul
            </div>
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="contact-mailto"
            className="hidden"
          >
            {CONTACT_EMAIL}
          </a>
        </aside>
      </div>
    </div>
  );
};

export default Contact;
